import { ref, computed, watch } from 'vue'
import { themes, themeGroups, type ThemeName, type Theme, type ThemeStyle, type ThemeMode } from '@/assets/themes'

// Globaler State für das aktuelle Theme
const currentThemeName = ref<ThemeName>('professional')
const currentThemeStyle = ref<ThemeStyle>('professional')
const currentThemeMode = ref<ThemeMode>('light')
// System theme functionality removed as requested
const isInitialized = ref(false)

// Theme-Einstellungen aus localStorage laden
const loadThemeFromStorage = () => {
  const savedThemeName = localStorage.getItem('theme') as ThemeName | null
  const savedThemeStyle = localStorage.getItem('themeStyle') as ThemeStyle | null
  const savedThemeMode = localStorage.getItem('themeMode') as ThemeMode | null
  // Überprüfen der gespeicherten Werte
  if (savedThemeStyle && themeGroups.styles.includes(savedThemeStyle as any)) {
    currentThemeStyle.value = savedThemeStyle
  }
  
  if (savedThemeMode && themeGroups.modes.includes(savedThemeMode)) {
    currentThemeMode.value = savedThemeMode
  }
  
  // Falls ein kompletter Theme-Name gespeichert ist, überschreiben
  if (savedThemeName && savedThemeName in themes) {
    currentThemeName.value = savedThemeName
    parseThemeName(savedThemeName)
  } else {
    // Andernfalls aus Style und Mode zusammensetzen
    updateThemeNameFromStyleAndMode()
  }
}

// Theme-Einstellungen in localStorage speichern
const saveThemeToStorage = (themeName: ThemeName, style: ThemeStyle, mode: ThemeMode) => {
  localStorage.setItem('theme', themeName)
  localStorage.setItem('themeStyle', style)
  localStorage.setItem('themeMode', mode)
}

// Theme-Name aus aktuellem Stil und Modus generieren
const updateThemeNameFromStyleAndMode = () => {
  // Generiere den Theme-Namen basierend auf Stil und Modus
  const themeName = currentThemeMode.value === 'dark'
    ? `${currentThemeStyle.value}Dark` as ThemeName
    : currentThemeStyle.value as ThemeName
  
  // Überprüfe, ob der generierte Name in den verfügbaren Themes existiert
  if (themeName in themes) {
    currentThemeName.value = themeName
  } else {
    // Fallback auf ein Standard-Theme
    currentThemeName.value = currentThemeMode.value === 'dark' ? 'dark' : 'light'
  }
}

// Stil und Modus aus einem Theme-Namen extrahieren
const parseThemeName = (themeName: ThemeName) => {
  if (themeName.includes('Dark')) {
    currentThemeMode.value = 'dark'
    const styleBase = themeName.replace('Dark', '') as ThemeStyle
    if (themeGroups.styles.includes(styleBase as any)) {
      currentThemeStyle.value = styleBase
    }
  } else {
    currentThemeMode.value = 'light'
    if (themeGroups.styles.includes(themeName as any)) {
      currentThemeStyle.value = themeName as ThemeStyle
    }
  }
}

// CSS-Variablen setzen
const applyTheme = (theme: Theme) => {
  const root = document.documentElement
  
  // Debug: Theme-Anwendung protokollieren
  console.log('[Theme] Applying theme:', theme.name)
  console.log('[Theme] Theme colors:', theme.colors)
  
  // Farben
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
    root.style.setProperty(cssVarName, value as string)
    
    // Debug: Spezifisch für backgroundSecondary und backgroundTertiary
    if (key === 'backgroundSecondary' || key === 'backgroundTertiary') {
      console.log(`[Theme] Setting ${cssVarName} = ${value}`)
      console.log(`[Theme] Computed value:`, getComputedStyle(root).getPropertyValue(cssVarName))
    }
  })
  
  // Schatten-Farbe explizit setzen
  root.style.setProperty('--color-shadow-color', theme.colors.shadowColor)
  
  // RGB-Varianten für Hintergrundfarben hinzufügen (für rgba in CSS)
  const getRgbValues = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `${r}, ${g}, ${b}`
  }
  
  // Nur für Hintergrundfarben, die in rgba() verwendet werden
  if (theme.colors.background && theme.colors.background.startsWith('#')) {
    root.style.setProperty('--color-background-rgb', getRgbValues(theme.colors.background))
  }
  if (theme.colors.backgroundLight && theme.colors.backgroundLight.startsWith('#')) {
    root.style.setProperty('--color-background-light-rgb', getRgbValues(theme.colors.backgroundLight))
  }
  if (theme.colors.backgroundDark && theme.colors.backgroundDark.startsWith('#')) {
    root.style.setProperty('--color-background-dark-rgb', getRgbValues(theme.colors.backgroundDark))
  }
  if (theme.colors.surface && theme.colors.surface.startsWith('#')) {
    root.style.setProperty('--color-surface-rgb', getRgbValues(theme.colors.surface))
  }
  
  // Schatten
  Object.entries(theme.shadows).forEach(([key, value]) => {
    root.style.setProperty(`--shadow-${key}`, value as string)
  })
  
  // Weitere Eigenschaften
  // Hinweis: --header-height wird jetzt zentral in variables.css definiert und nicht mehr hier gesetzt
  // um Konflikte zu vermeiden
  root.style.setProperty('--sidebar-width', theme.properties.sidebarWidth)
  
  // Border Radius
  Object.entries(theme.properties.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(`--border-radius-${key}`, value as string)
  })
  
  // Transitions
  Object.entries(theme.properties.transitions).forEach(([key, value]) => {
    root.style.setProperty(`--transition-${key}`, value as string)
  })
  
  // Typography anwenden, falls vorhanden
  if (theme.properties.fonts) {
    // Schriftfamilie
    if (theme.properties.fonts.family) {
      root.style.setProperty('--font-family-main', theme.properties.fonts.family)
      root.style.setProperty('--font-family-headings', theme.properties.fonts.family)
    }
    
    // Schriftgrößen
    if (theme.properties.fonts.sizes) {
      Object.entries(theme.properties.fonts.sizes).forEach(([key, value]) => {
        root.style.setProperty(`--font-size-${key}`, value as string)
      })
    }
    
    // Schriftgewichte
    if (theme.properties.fonts.weights) {
      Object.entries(theme.properties.fonts.weights).forEach(([key, value]) => {
        root.style.setProperty(`--font-weight-${key}`, value as string)
      })
    }
  }
  
  // Button-Styles anwenden, falls vorhanden
  if (theme.properties.buttons) {
    // Iteriere über die Button-Typen (primary, secondary, etc.)
    Object.entries(theme.properties.buttons).forEach(([buttonType, buttonProps]) => {
      // Iteriere über die Properties jedes Button-Typs (background, text, hover)
      if (buttonProps && typeof buttonProps === 'object') {
        Object.entries(buttonProps).forEach(([propName, propValue]) => {
          root.style.setProperty(`--button-${buttonType}-${propName}`, propValue as string)
        })
      }
    })
  }
  
  // Alert-Styles anwenden, falls vorhanden
  if (theme.properties.alerts) {
    // Iteriere über die Alert-Typen (success, error, etc.)
    Object.entries(theme.properties.alerts).forEach(([alertType, alertProps]) => {
      // Iteriere über die Properties jedes Alert-Typs
      if (alertProps && typeof alertProps === 'object') {
        Object.entries(alertProps).forEach(([propName, propValue]) => {
          root.style.setProperty(`--alert-${alertType}-${propName}`, propValue as string)
        })
      } else if (typeof alertProps === 'string') {
        // Falls es sich um einfache String-Werte handelt
        root.style.setProperty(`--alert-${alertType}`, alertProps)
      }
    })
  }
  
  // Theme-Klassen auf body setzen - mit classList statt className, um andere Klassen zu erhalten
  const body = document.body;
  
  // Entferne alte Theme-Klassen
  document.body.classList.forEach(className => {
    if (className.startsWith('theme-') || className.startsWith('style-') || className.startsWith('mode-')) {
      body.classList.remove(className);
    }
  });
  
  // Füge neue Theme-Klassen hinzu
  body.classList.add(`theme-${theme.name}`);
  body.classList.add(`style-${currentThemeStyle.value}`);
  body.classList.add(`mode-${currentThemeMode.value}`);
}

// Theme beim Start laden
loadThemeFromStorage()

export const useTheme = () => {
  
  // System-Präferenz für Dark Mode erkennen
  const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)')
  const systemPrefersDark = ref(prefersDarkMode.matches)
  
  prefersDarkMode.addEventListener('change', (e) => {
    systemPrefersDark.value = e.matches
  })
  
  const currentTheme = computed(() => themes[currentThemeName.value])
  
  // Theme-Funktionen definieren
  const setTheme = (themeName: ThemeName) => {
    if (themeName in themes) {
      currentThemeName.value = themeName
      parseThemeName(themeName)
      
      saveThemeToStorage(
        themeName,
        currentThemeStyle.value,
        currentThemeMode.value
      )
    }
  }
  
  // Initialisierung beim ersten Aufruf
  if (!isInitialized.value) {
    // Standard-Theme setzen, wenn kein Theme im localStorage gefunden wurde
    if (!(currentThemeName.value in themes)) {
      setTheme('professional')
    } else {
      // Theme anwenden
      applyTheme(themes[currentThemeName.value])
    }
    
    isInitialized.value = true
  }
  
  const setThemeStyle = (style: ThemeStyle) => {
    if (themeGroups.styles.includes(style as any)) {
      currentThemeStyle.value = style
      updateThemeNameFromStyleAndMode()
      
      // In localStorage speichern
      saveThemeToStorage(
        currentThemeName.value,
        style,
        currentThemeMode.value
      )
      
      // Theme sofort anwenden
      applyTheme(themes[currentThemeName.value])
    }
  }
  
  const setThemeMode = (mode: ThemeMode) => {
    if (themeGroups.modes.includes(mode)) {
      currentThemeMode.value = mode
      updateThemeNameFromStyleAndMode()
      
      // In localStorage speichern
      saveThemeToStorage(
        currentThemeName.value,
        currentThemeStyle.value,
        mode
      )
      
      // Theme sofort anwenden
      applyTheme(themes[currentThemeName.value])
    }
  }
  
  const toggleTheme = () => {
    const newMode = currentThemeMode.value === 'light' ? 'dark' : 'light'
    setThemeMode(newMode)
  }
  
  // Theme anwenden, wenn es sich ändert
  watch(currentTheme, (theme) => {
    applyTheme(theme)
  }, { immediate: true })
  
  // System theme functionality removed as requested
  
  return {
    // Theme-Objekt und Name
    currentTheme,
    currentThemeName: computed(() => currentThemeName.value),
    
    // Theme-Stil und Modus
    currentThemeStyle: computed(() => currentThemeStyle.value),
    currentThemeMode: computed(() => currentThemeMode.value),
    // System theme functionality removed as requested
    
    // Verfügbare Themes
    themes: computed(() => themes),
    themeGroups: computed(() => themeGroups),
    
    // Setter
    setTheme,
    setThemeStyle,
    setThemeMode,
    toggleTheme,
    // System theme functionality removed as requested
    
    // System-Theme
    systemPrefersDark: computed(() => systemPrefersDark.value),
  }
}
